import { useState } from "react";
import { Images, Sparkles, Loader2, Download, Lightbulb, ChevronDown, ChevronUp } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";

/* Receipt Gallery — every scanned/attached receipt for this trip in one browsable
   grid with quick download. */
export function ReceiptGallery({ items, token }) {
  const [open, setOpen] = useState(false);
  const receipts = (items || []).filter((e) => e.receipt_url);
  if (!receipts.length) return null;
  const url = (e) => `${process.env.REACT_APP_BACKEND_URL}${e.receipt_url}?auth=${token}`;
  return (
    <div className="mt-4 border border-zinc-300 bg-white" data-testid="receipt-gallery">
      <button onClick={() => setOpen((o) => !o)} data-testid="receipt-gallery-toggle"
        className="w-full flex items-center gap-2 px-4 py-3 text-left hover:bg-zinc-50">
        <Images className="w-4 h-4 text-[#b45309]" />
        <span className="font-700 text-sm">Receipt Gallery</span>
        <span className="text-xs text-muted-foreground">{receipts.length} receipt{receipts.length === 1 ? "" : "s"}</span>
        {open ? <ChevronUp className="w-4 h-4 ml-auto text-muted-foreground" /> : <ChevronDown className="w-4 h-4 ml-auto text-muted-foreground" />}
      </button>
      {open && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 p-4 pt-0">
          {receipts.map((e) => (
            <div key={e.id} className="border border-zinc-200 group" data-testid={`receipt-card-${e.id}`}>
              <a href={url(e)} target="_blank" rel="noreferrer" title="Open full size">
                <img src={url(e)} alt={e.merchant || "Receipt"} loading="lazy"
                  className="w-full h-28 object-cover bg-zinc-100" />
              </a>
              <div className="p-2">
                <div className="text-xs font-700 truncate">{e.merchant || e.description || "Receipt"}</div>
                <div className="flex items-center justify-between text-[11px] text-muted-foreground">
                  <span>{e.date || ""} · {e.amount} {e.currency}</span>
                  <a href={url(e)} download={`receipt-${e.date || e.id}.jpg`} data-testid={`receipt-download-${e.id}`}
                    title="Download receipt" className="text-[#b45309] hover:text-amber-600"><Download className="w-3.5 h-3.5" /></a>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* AI Spending Insights — savings opportunities from this trip's spending patterns. */
export function SpendingInsights({ tripId }) {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const run = async () => {
    setBusy(true);
    try { setResult((await api.post(`/expenses/trip/${tripId}/insights`)).data); }
    catch (e) { toast.error(e?.response?.data?.detail || "AI insights unavailable — try again"); }
    setBusy(false);
  };
  return (
    <div className="mt-4" data-testid="spending-insights">
      {!result && (
        <button onClick={run} disabled={busy} data-testid="spending-insights-btn"
          className="inline-flex items-center gap-1.5 border border-violet-400 text-violet-700 hover:bg-violet-50 px-3 py-2 text-sm font-700 disabled:opacity-50">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
          {busy ? "Analyzing your spending…" : "AI Spending Insights — where could you save?"}
        </button>
      )}
      {result && (
        <div className="border border-violet-200 bg-violet-50/40 p-4" data-testid="spending-insights-result">
          <div className="flex items-center gap-2 mb-2">
            <Sparkles className="w-4 h-4 text-violet-600" />
            <span className="font-700 text-sm">{result.headline}</span>
          </div>
          <div className="space-y-2">
            {(result.insights || []).map((i, idx) => (
              <div key={idx} className="bg-white border border-zinc-200 p-3" data-testid={`spending-insight-${idx}`}>
                <div className="flex items-start gap-2">
                  <Lightbulb className="w-4 h-4 text-amber-500 mt-0.5 shrink-0" />
                  <div className="min-w-0">
                    <div className="font-700 text-sm">{i.title}</div>
                    <p className="text-xs text-muted-foreground mt-0.5">{i.detail}</p>
                    {i.potential_saving && <span className="inline-block mt-1 text-[11px] font-700 bg-emerald-50 text-emerald-700 px-1.5 py-0.5">Potential saving: {i.potential_saving}</span>}
                  </div>
                </div>
              </div>
            ))}
          </div>
          <button onClick={run} disabled={busy} className="mt-2 text-xs font-700 text-violet-700 hover:underline" data-testid="spending-insights-rerun">
            {busy ? "Re-analyzing…" : "Re-analyze"}
          </button>
        </div>
      )}
    </div>
  );
}
